import { formatDistanceToNow, type Locale } from "date-fns";

import type { ReactNode } from "react";

import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";

import { parseDate } from "@/lib/parseDate";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const PLATFORM_KEY: Record<string, string> = {
  windows: "USERS.platformWindows",

  macos: "USERS.platformMacos",

  linux: "USERS.platformLinux",
};

// i18n key for a desktop platform reported by the shell (windows | macos | linux),
// or null when the value is not one we know (callers fall back to the raw string).
export const platformLabelKey = (platform: string | null | undefined) =>
  platform ? (PLATFORM_KEY[platform] ?? null) : null;

export type DesktopInstall = {
  platform: string;

  version?: string | null;

  seenAt?: string | null;

  uninstalledAt?: string | null;

  /**
   * La versión DEL AVISO de desinstalación, que no siempre es `version`.
   *
   * `version` es la última que reportó esa instalación, y desde que un reporte
   * atrasado ya no borra una marca más nueva, las dos pueden diferir. Usar
   * `version` acá mostraba como desinstalada una versión que la persona no
   * desinstaló.
   */
  uninstalledVersion?: string | null;
};

type DesktopVersionCellProps = {
  installs?: DesktopInstall[];

  dateLoc: Locale;
};

// No TooltipProvider of its own: this cell is in every row of both users tabs
// (up to 300 in all users), and one provider per cell grew with the table. The
// table around it brings one TableTooltipProvider.
const WithTooltip = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span className="inline-flex cursor-help items-center gap-2">
        {children}
      </span>
    </TooltipTrigger>
    <TooltipContent className="border-neutral-700 bg-neutral-900 text-neutral-300">
      {label}
    </TooltipContent>
  </Tooltip>
);

/**
 * Qué tiene instalado cada cuenta, UNA LÍNEA POR INSTALACIÓN.
 *
 * Antes era una sola: el backend tenía un casillero por cuenta, así que quien
 * usa Eriol en la PC y en la Mac aparecía como una instalación sola y las dos
 * se pisaban. Peor: desinstalar en Windows marcaba la CUENTA, y acá se leía
 * "se fue" aunque siguiera usando la Mac todos los días.
 *
 * Por eso la plataforma va PEGADA a la marca de desinstalación y no suelta en
 * otro lado: lo que hay que poder leer de un vistazo es "desinstaló Windows,
 * sigue en macOS", que es la pregunta que esta columna existe para contestar.
 */
const DesktopVersionCell = ({
  installs,
  dateLoc,
}: DesktopVersionCellProps) => {
  const { t } = useTranslation("common");

  const relative = (iso: string | null | undefined) => {
    const date = parseDate(iso);

    return date
      ? formatDistanceToNow(date, { addSuffix: true, locale: dateLoc })
      : null;
  };

  const nombreDe = (platform: string) => {
    const key = platformLabelKey(platform);

    return key ? t(key) : platform;
  };

  if (!installs?.length) {
    return (
      <span className="text-xs text-neutral-500">
        {t("USERS.versionUnknown")}
      </span>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      {installs.map((install) => {
        const plataforma = nombreDe(install.platform);

        if (install.uninstalledAt) {
          // Con la versión, no solo la plataforma: es lo que dice CUÁL se fue
          // cuando alguien reinstala y desinstala varias veces, y sin ella la
          // celda no la muestra en ningún otro lado.
          //
          // Y es la versión DEL AVISO, no `version`: esa es la última reportada
          // y puede ser otra —un reporte atrasado mueve `version` y conserva la
          // marca—, así que acá decía "desinstaló Windows v0.8.30" cuando el
          // aviso era de 0.8.29. Si el aviso es viejo y no la trae, se muestra
          // sólo la plataforma en vez de inventar una.
          const versionDelAviso = install.uninstalledVersion;

          const badge = (
            <>
              <Badge className="bg-red-500/20 text-red-400">
                {t("USERS.uninstalled")}
              </Badge>
              <span className="text-xs text-neutral-400">
                {versionDelAviso
                  ? `${plataforma} v${versionDelAviso}`
                  : plataforma}
              </span>
            </>
          );

          const when = relative(install.uninstalledAt);

          return (
            <span key={install.platform} className="inline-flex items-center gap-2">
              {when ? (
                <WithTooltip label={t("USERS.uninstalledAt", { when })}>
                  {badge}
                </WithTooltip>
              ) : (
                badge
              )}
            </span>
          );
        }

        const contenido = (
          <>
            <Badge className="bg-violet-500/20 text-violet-400">
              {install.version ? `v${install.version}` : plataforma}
            </Badge>
            {install.version && (
              <span className="text-xs text-neutral-400">{plataforma}</span>
            )}
          </>
        );

        const when = relative(install.seenAt);

        return (
          <span key={install.platform} className="inline-flex items-center gap-2">
            {when ? (
              <WithTooltip label={t("USERS.versionSeen", { when })}>
                {contenido}
              </WithTooltip>
            ) : (
              contenido
            )}
          </span>
        );
      })}
    </div>
  );
};

export default DesktopVersionCell;
